import { useReactiveVar } from '@apollo/client'
import CloseIcon from '@mui/icons-material/Close'
import { useTheme, useMediaQuery } from '@mui/material'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import { useNavigate, useParams } from 'react-router-dom'
import {
  BucketAlbumFragmentFragmentDoc,
  BucketFragmentFragmentDoc,
  BucketMediaByAlbumIdDocument,
  BucketUserRole
} from '../../../../graphql/generated'
import { activeAlbumIdVar, modalVar } from '../../../../providers/apollo/cache'
import logger from '../../../../utils/logger'
import useBucket from '../../hooks/useBucket'
import useBucketAlbumsMove from '../../hooks/useBucketAlbumsMove'
import useBucketGallerySettings from '../../hooks/useBucketGallerySettings'
import useSelectedAlbums from '../../hooks/useSelectedAlbums'

export default function AlbumDeleteModal() {
  const { bucketId } = useParams<{ bucketId: string }>()
  const { mutation } = useBucketAlbumsMove()
  const { bucketUser, isFreemiumBucket } = useBucket()
  const isAdminOrOwner = bucketUser?.userRole === BucketUserRole.Admin || bucketUser?.userRole === BucketUserRole.Owner
  const modal = useReactiveVar(modalVar)
  const { settings } = useBucketGallerySettings()
  const { filterBy, sortBy } = settings
  const { selectedAlbums, clearSelectedAlbums } = useSelectedAlbums()
  const activeAlbumId = useReactiveVar(activeAlbumIdVar)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const count = Math.max(selectedAlbums.size, 1)
  const noun = count === 1 ? 'album' : 'albums'
  const navigate = useNavigate()

  const handleClose = () => {
    modalVar(null)
  }

  const handleSubmit = (action: 'saveContent' | 'deleteContent') => () => {
    const albumIds = activeAlbumId ? [activeAlbumId] : Array.from(selectedAlbums.keys())
    logger.debug('Delete Albums', albumIds)
    if (!bucketId) return
    mutation({
      variables: {
        bucketId,
        albumIds,
        data: {
          deleteAlbum: true,
          moveMediaToTrash: action === 'deleteContent'
        }
      },
      update: (cache, { data }) => {
        if (!data?.bucketAlbumsMove) return

        data.bucketAlbumsMove?.forEach((albumId) => {
          const bucketAlbum = cache.readFragment({
            id: `BucketAlbum:${albumId}`,
            fragment: BucketAlbumFragmentFragmentDoc
          })

          const bucketAlbumId = bucketAlbum?.bucketId !== bucketAlbum?.parentId ? bucketAlbum?.parentId : undefined

          // Update the Parent Bucket Album's counters if it exists
          cache.updateFragment({ id: `BucketAlbum:${bucketAlbumId}`, fragment: BucketAlbumFragmentFragmentDoc }, (current) => {
            if (!current) return null

            const subAlbumCount = Math.max((current.subAlbumCount || 0) - 1, 0)
            const totalSubalbums = Math.max((current.counters?.totalSubalbums || 0) - 1, 0)

            return {
              ...current,
              subAlbumCount,
              counters: {
                ...current.counters,
                totalSubalbums
              }
            }
          })

          if (action === 'deleteContent') {
            const album = cache.readQuery({
              query: BucketMediaByAlbumIdDocument,
              variables: {
                albumId,
                bucketId,
                filterBy,
                sortBy
              }
            })

            const albumItems = album?.bucketMediaByAlbumId?.items || []

            albumItems.forEach((media) => {
              // Modify bucket media count
              cache.updateFragment(
                {
                  id: `Bucket:${bucketId}`,
                  fragment: BucketFragmentFragmentDoc
                },
                (current) => {
                  if (!current) return null

                  const totalMediaMinusTrash = current.counters?.totalMediaMinusTrash ?? 0
                  const totalMediaInTrash = current?.counters?.totalMediaInTrash ?? 0
                  const fileSize = media?.fileSize ?? 0
                  const totalSizeInTrash = current?.counters?.totalSizeInTrash ?? 0

                  return {
                    ...current,
                    counters: {
                      ...current.counters,
                      totalMediaMinusTrash: totalMediaMinusTrash - 1,
                      totalMediaInTrash: totalMediaInTrash + 1,
                      totalSizeInTrash: totalSizeInTrash + fileSize
                    }
                  }
                }
              )

              // Remove media from bucket
              cache.updateQuery(
                {
                  query: BucketMediaByAlbumIdDocument,
                  variables: {
                    bucketId,
                    filterBy,
                    sortBy
                  }
                },
                (cachedData) => {
                  if (!media || !cachedData) return null

                  const { bucketMediaByAlbumId } = cachedData
                  const currentItems = bucketMediaByAlbumId?.items || []
                  const items = currentItems.filter((item) => item.id !== media.id)

                  return {
                    bucketMediaByAlbumId: {
                      ...bucketMediaByAlbumId,
                      items
                    }
                  }
                }
              )
            })
          }
          cache.evict({ id: cache.identify({ __typename: 'BucketAlbum', id: albumId }) })
          cache.gc()
        })
      },
      onCompleted: () => {
        clearSelectedAlbums()
        modalVar(null)
      }
    })
    activeAlbumIdVar(null)
    navigate(`/bucket/${bucketId}`)
  }

  return (
    <Dialog
      data-test="delete-modal"
      open={modal === 'bucketAlbumDelete'}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: '16px'
        }
      }}
    >
      <CloseIcon sx={{ position: 'absolute', cursor: 'pointer', right: 10, top: 10 }} onClick={handleClose} data-test="close-modal" />
      <DialogContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          padding: '24px 20px 16px 20px',
          textAlign: 'center',
          overflow: 'hidden',
          [theme.breakpoints.down('md')]: {
            padding: '20px 16px'
          }
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 24, lineHeight: '28px', color: '#081230', marginBottom: '16px' }}>Delete this Album?</Typography>
        <Typography sx={{ color: '#2F364E', lineHeight: '20px', marginBottom: isFreemiumBucket ? '10px' : '24px', maxWidth: '100%' }}>
          {isFreemiumBucket
            ? `You are about to delete ${count} ${noun}. The contents will stay in the bucket. This action cannot be undone.\n\nWould you like to continue?`
            : `You are about to delete ${count} ${noun}. This action cannot be undone. Would you like to continue?`}
        </Typography>
        <div
          style={{
            width: 'calc(100% + 64px)',
            height: '1px',
            backgroundColor: '#E0E0E0',
            marginBottom: '10px',
            marginLeft: '-32px',
            marginRight: '-32px'
          }}
        />
        <DialogActions
          sx={{
            width: '100%',
            justifyContent: isFreemiumBucket ? 'flex-end' : 'center',
            flexDirection: isFreemiumBucket ? 'row' : 'column',
            gap: isFreemiumBucket ? '5px' : '8px',
            [theme.breakpoints.down('md')]: {
              flexDirection: 'column'
            },
            '& button': {
              height: '40px',
              [theme.breakpoints.down('md')]: {
                width: '100%',
                margin: '2px 0'
              }
            }
          }}
        >
          {isFreemiumBucket ? (
            <>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: '25px',
                  borderColor: '#1976D2',
                  color: '#1976D2',
                  width: '100px',
                  '&:hover': {
                    borderColor: '#1565C0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
                onClick={handleClose}
                data-test="cancel-modal"
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={{
                  borderRadius: '25px',
                  backgroundColor: '#D32F2F',
                  width: '140px',
                  '&:hover': {
                    backgroundColor: '#B71C1C'
                  }
                }}
                onClick={handleSubmit('saveContent')}
                data-testid="album-confirm-delete-album-and-contents"
                disabled={!isAdminOrOwner}
              >
                Delete Album
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="album-confirm-delete-album-keep-contents"
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: '25px',
                  width: '100%',
                  borderColor: '#D32F2F',
                  color: '#D32F2F',
                  '&:hover': {
                    borderColor: '#B71C1C',
                    backgroundColor: 'rgba(211, 47, 47, 0.04)'
                  }
                }}
                onClick={handleSubmit('saveContent')}
                data-test="secondary-confirm-modal"
                disabled={!isAdminOrOwner}
              >
                Delete {noun} and keep its content{!isMobile && ' in the bucket'}
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={{
                  borderRadius: '25px',
                  width: '100%',
                  backgroundColor: '#D32F2F',
                  '&:hover': {
                    backgroundColor: '#B71C1C'
                  }
                }}
                onClick={handleSubmit('deleteContent')}
                data-testid="album-confirm-delete-album-and-contents"
                disabled={!isAdminOrOwner}
              >
                Delete {noun} and its contents
              </Button>
            </>
          )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}
